import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const LOCKED_OPTIONS_COLLAPSE_DURATION_MS = 220

interface Props {
  disabled: boolean
  selectedIndices: Set<number>
  optionsLength: number
}

interface ReturnValue {
  isExpanded: boolean
  isAnimatingCollapse: boolean
  shouldRenderAllOptions: boolean
  shouldShowSummaryTile: boolean
  shouldShowToggleButton: boolean
  collapsedSelectedIndices: Array<number>
  handleExpand: () => void
  handleCollapse: () => void
  shouldHideOption: (index: number) => boolean
}

export function useLockedOptionsPreview({
  disabled,
  selectedIndices,
  optionsLength,
}: Props): ReturnValue {
  const [isExpanded, setIsExpanded] = useState(!disabled)
  const [isAnimatingCollapse, setIsAnimatingCollapse] = useState(false)
  const collapseTimeoutRef = useRef<number | undefined>(undefined)
  const previousDisabledRef = useRef(disabled)

  const hasSelection = selectedIndices.size > 0

  const clearCollapseTimeout = useCallback(() => {
    if (collapseTimeoutRef.current !== undefined) {
      window.clearTimeout(collapseTimeoutRef.current)
      collapseTimeoutRef.current = undefined
    }
  }, [])

  const startCollapseAnimation = useCallback(() => {
    clearCollapseTimeout()
    setIsExpanded(false)
    setIsAnimatingCollapse(true)
    collapseTimeoutRef.current = window.setTimeout(() => {
      setIsAnimatingCollapse(false)
      collapseTimeoutRef.current = undefined
    }, LOCKED_OPTIONS_COLLAPSE_DURATION_MS)
  }, [clearCollapseTimeout])

  useEffect(() => {
    return () => {
      clearCollapseTimeout()
    }
  }, [clearCollapseTimeout])

  useEffect(() => {
    const wasDisabled = previousDisabledRef.current
    previousDisabledRef.current = disabled

    if (!disabled) {
      clearCollapseTimeout()
      setIsAnimatingCollapse(false)
      setIsExpanded(true)
      return
    }

    if (!wasDisabled) {
      startCollapseAnimation()
      return
    }

    clearCollapseTimeout()
    setIsAnimatingCollapse(false)
    setIsExpanded(false)
  }, [disabled, clearCollapseTimeout, startCollapseAnimation])

  const handleExpand = useCallback(() => {
    clearCollapseTimeout()
    setIsAnimatingCollapse(false)
    setIsExpanded(true)
  }, [clearCollapseTimeout])

  const handleCollapse = useCallback(() => {
    if (!disabled) {
      return
    }
    startCollapseAnimation()
  }, [disabled, startCollapseAnimation])

  const collapsedSelectedIndices = useMemo(() => {
    return Array.from(selectedIndices).sort((left, right) => left - right)
  }, [selectedIndices])

  const shouldRenderAllOptions = !disabled || isExpanded || isAnimatingCollapse
  const shouldShowSummaryTile = disabled && !isExpanded && !isAnimatingCollapse && !hasSelection
  const shouldShowToggleButton = disabled && !isAnimatingCollapse && (hasSelection || isExpanded)

  const shouldHideOption = useCallback((index: number) => {
    if (!disabled || isExpanded || !shouldRenderAllOptions) {
      return false
    }

    if (!hasSelection) {
      return optionsLength > 0
    }

    return !selectedIndices.has(index)
  }, [disabled, hasSelection, isExpanded, optionsLength, selectedIndices, shouldRenderAllOptions])

  return {
    isExpanded,
    isAnimatingCollapse,
    shouldRenderAllOptions,
    shouldShowSummaryTile,
    shouldShowToggleButton,
    collapsedSelectedIndices,
    handleExpand,
    handleCollapse,
    shouldHideOption,
  }
}

export const LOCKED_OPTIONS_COLLAPSE_DURATION = LOCKED_OPTIONS_COLLAPSE_DURATION_MS
